// 9fbef606107a605d69c0edbcd8029e5d
/** @module LabeledRadioButton
 * @summary A React component rendering an RadioButton field
 *
 */
import React from 'react';
import { PropTypes } from 'prop-types';
import styles from '../styles/LabeledRadioButton.style';
import withStyles from '../../../hoc/withStyles';
import BodyCopy from '../../BodyCopy';

/**
 * @summary A React component rendering an checkbox field with a  optional data .
 *
 * Supports <code>input</code> and <code>meta</code> props passed down by a wrappping {@linkcode module:redux-form.Field} HOC.
 * Any extra props (i.e., other than <code>title, subTitle, type, className, meta</code>),
 * e.g., <code>value, disabled, placeholder</code>, passed to this component will be passed along to the rendered <code>input</code> element.
 */
class LabeledRadioButton extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    className: PropTypes.string,
    variation: PropTypes.string,
    /** The value of the radioButton (i.e., the value of the associated form field when this radio button is checked) */
    selectedValue: PropTypes.string.isRequired,
    children: PropTypes.node,
    name: PropTypes.string,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    input: PropTypes.shape({}),
    topPosition: PropTypes.string,
    midtitle: PropTypes.string,
    spc: PropTypes.bool,
  };

  static defaultProps = {
    title: null,
    subtitle: null,
    className: '',
    variation: 'primary',
    children: null,
    name: '',
    checked: false,
    disabled: false,
    input: null,
    topPosition: '0',
    midtitle: null,
    spc: false,
  };

  static labeledRadioButtonCounter = 0;

  constructor(props) {
    super(props);
    LabeledRadioButton.labeledRadioButtonCounter += 1;
    this.labeledRadioButtonCounter = LabeledRadioButton.labeledRadioButtonCounter;
    this.customOnChange = this.customOnChange.bind(this);
  }

  customOnChange(e) {
    const { input } = this.props;
    const event = { ...e };
    setTimeout(() => {
      input.onChange(event);
    }, 0);
  }

  // eslint-disable-next-line complexity
  render() {
    const {
      title,
      subtitle,
      midtitle,
      spc,
      className,
      input, // When using redux-form, is passed implicitly by a wrapping Field component
      children,
      disabled,
      selectedValue,
      checked,
      name,
      variation,
      topPosition,
      customReduxAction,
      ...otherProps // all the extra props passed to this component (or the wrapping Field component when using redux-form)
    } = this.props;
    const inputName = input && input.name ? input.name : name;
    const id = `${inputName}_${this.labeledRadioButtonCounter}`;
    const isChecked = (input && selectedValue === input.value) || checked;
    const inputProps = { ...input };
    if (customReduxAction) {
      inputProps.onChange = this.customOnChange;
    }
    return (
      <label htmlFor={id} className={className} isChecked={isChecked}>
        <div className={`radio-button${isChecked ? '-checked' : ''}`}>
          <input
            {...inputProps}
            {...otherProps}
            id={id}
            disabled={disabled}
            type="radio"
            className={disabled ? 'disabled' : null}
            value={selectedValue}
            checked={isChecked}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            name={inputName}
          />
        </div>
        {children && <div className="radio-button-child-content">{children}</div>}
        {title && (
          <BodyCopy
            className="input-radio-title"
            component="span"
            fontFamily="secondary"
            fontSize="fs16"
            dataLocator={title}
          >
            {title}
          </BodyCopy>
        )}
        {spc && (
          <BodyCopy
            className="middle-title"
            component="div"
            fontFamily="secondary"
            fontSize="fs12"
            dataLocator={midtitle}
            color="green.500"
          >
            {midtitle}
          </BodyCopy>
        )}
        {subtitle && (
          <>
            <BodyCopy
              className="input-subtitle"
              component="span"
              fontFamily="secondary"
              fontSize="fs12"
              dataLocator={subtitle}
            >
              {subtitle}
            </BodyCopy>
          </>
        )}
      </label>
    );
  }
}

export default withStyles(LabeledRadioButton, styles);
export { LabeledRadioButton as LabeledRadioButtonVanilla };
